import { useEffect, useState } from 'react';
import axios from 'axios';
import { Globe, Trash2, Plus } from 'lucide-react';

interface PublicIp {
  id: number;
  tenant_id: number;
  cidr: string;
  label: string | null;
  site_id: number | null;
  site_name: string | null;
}

interface SiteOption { id: number; name: string }

// IP pubblici del cliente (indirizzi di uscita Internet, servizi esposti), per sede del tenant:
// un alert con uno di questi come IP sorgente è il cliente stesso, non un attaccante esterno —
// Security Alerts, Security Incidents e i dettagli lo marcano "Customer · <sede>" al posto del
// paese. Gestito da Sites & Brokers; API /api/tenant-public-ips.
export default function CustomerPublicIpsPanel({ tenantId, sites }: { tenantId: number | null; sites: SiteOption[] }) {
  const [items, setItems] = useState<PublicIp[]>([]);
  const [cidr, setCidr] = useState('');
  const [label, setLabel] = useState('');
  const [siteId, setSiteId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    if (!tenantId) { setItems([]); return; }
    const res = await axios.get('/api/tenant-public-ips', { params: { tenantId } });
    setItems(res.data.data);
  }
  useEffect(() => { load(); }, [tenantId]);
  // Con una sola sede la si propone subito; con più sedi l'analista sceglie.
  useEffect(() => { if (!siteId && sites.length === 1) setSiteId(String(sites[0].id)); }, [sites]);

  async function add() {
    if (!tenantId || !cidr.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await axios.post('/api/tenant-public-ips', {
        tenantId, cidr: cidr.trim(), label: label.trim() || undefined, siteId: siteId ? Number(siteId) : undefined,
      });
      setCidr(''); setLabel('');
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error while saving');
    } finally {
      setSaving(false);
    }
  }

  async function changeSite(item: PublicIp, value: string) {
    await axios.put(`/api/tenant-public-ips/${item.id}`, { siteId: value ? Number(value) : null });
    await load();
  }

  async function remove(item: PublicIp) {
    if (!window.confirm(`Remove ${item.cidr}${item.label ? ` (${item.label})` : ''} from the customer's public IPs?`)) return;
    await axios.delete(`/api/tenant-public-ips/${item.id}`);
    await load();
  }

  if (!tenantId) return null;

  return (
    <div className="card overflow-hidden mb-4">
      <div className="px-4 py-2.5 border-b border-gray-100">
        <h2 className="font-medium text-sm text-gray-900 inline-flex items-center gap-2"><Globe size={15} /> Customer public IPs</h2>
        <p className="text-xs text-gray-400">
          The customer's own public addresses (Internet breakout, exposed services), per site. Alerts and incidents
          whose source IP falls in one of these blocks are marked "Customer · site" instead of showing a country, so
          traffic from the customer's own sites is not mistaken for an external attacker. A single IP is stored as /32;
          "Whole tenant" means the address is not tied to a specific site.
        </p>
      </div>
      {error && <div className="px-4 py-2 text-xs text-red-600 bg-red-50">{error}</div>}
      <table className="w-full text-sm">
        <thead><tr>
          <th className="table-header">Site</th><th className="table-header">IP / CIDR</th><th className="table-header">Label</th><th className="table-header w-10"></th>
        </tr></thead>
        <tbody>
          {items.map(item => (
            <tr key={item.id} className="border-t border-gray-100">
              <td className="table-cell">
                <select value={item.site_id ?? ''} onChange={e => changeSite(item, e.target.value)} className="input-field py-0.5 px-1.5 text-xs w-52">
                  <option value="">Whole tenant</option>
                  {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </td>
              <td className="table-cell font-mono text-xs text-gray-700">{item.cidr}</td>
              <td className="table-cell text-gray-700">{item.label || <span className="text-gray-400">—</span>}</td>
              <td className="table-cell">
                <button onClick={() => remove(item)} className="p-1 text-gray-400 hover:text-red-600" title="Remove"><Trash2 size={14} /></button>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr><td className="table-cell text-gray-400" colSpan={4}>No public IP configured for this tenant</td></tr>
          )}
          <tr className="border-t border-gray-100 bg-gray-50/50">
            <td className="table-cell">
              <select value={siteId} onChange={e => setSiteId(e.target.value)} className="input-field py-0.5 px-1.5 text-xs w-52">
                <option value="">Whole tenant</option>
                {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </td>
            <td className="table-cell">
              <input value={cidr} onChange={e => setCidr(e.target.value)} placeholder="203.0.113.7 or 203.0.113.0/29"
                onKeyDown={e => { if (e.key === 'Enter') add(); }} className="input-field py-0.5 px-1.5 text-xs font-mono w-56" />
            </td>
            <td className="table-cell">
              <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Label (e.g. firewall, mail server)"
                onKeyDown={e => { if (e.key === 'Enter') add(); }} className="input-field py-0.5 px-1.5 text-xs w-56" />
            </td>
            <td className="table-cell">
              <button onClick={add} disabled={saving || !cidr.trim()} className="btn-primary py-0.5 px-2 text-xs inline-flex items-center gap-1 disabled:opacity-50">
                <Plus size={12} /> Add
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
